import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cx } from "./format";

const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const set = new Set([0, total - 1, current - 1, current, current + 1].filter((p) => p >= 0 && p < total));
  const pages = [...set].sort((a, b) => a - b);
  const out = [];
  pages.forEach((p, i) => {
    if (i && p - pages[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
};

const NavBtn = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="w-8 h-8 grid place-items-center rounded-md text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

/**
 * Page controls with a "Showing 1–10 of 240" summary and rows-per-page.
 * Zero-based `page`. Used by DataTable and by screens with custom tables.
 */
export const Pagination = ({
  page, pageSize, total, onPageChange, onPageSizeChange, pageSizeOptions = [10, 20, 50], className,
}) => {
  const pages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  const from = total ? page * pageSize + 1 : 0;
  const to = Math.min(total || 0, (page + 1) * pageSize);
  return (
    <div className={cx("flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-cm-border text-[13px] text-cm-muted", className)}>
      <span className="tabular-nums">
        {total ? <>Showing <b className="text-cm-text font-semibold">{from}–{to}</b> of <b className="text-cm-text font-semibold">{total.toLocaleString("en-IN")}</b></> : "No rows"}
      </span>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <label className="hidden sm:flex items-center gap-2">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-md border border-cm-border-strong bg-cm-surface-2 text-cm-text px-2 text-[13px] focus:outline-none focus:border-cm-gold"
            >
              {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
        <nav className="flex items-center gap-0.5" aria-label="Pagination">
          <NavBtn onClick={() => onPageChange(0)} disabled={page <= 0} label="First page"><ChevronsLeft size={16} /></NavBtn>
          <NavBtn onClick={() => onPageChange(page - 1)} disabled={page <= 0} label="Previous page"><ChevronLeft size={16} /></NavBtn>
          {pageWindow(page, pages).map((p) =>
            typeof p === "string" ? (
              <span key={p} className="w-6 text-center">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                aria-current={p === page ? "page" : undefined}
                className={cx(
                  "min-w-[32px] h-8 px-1.5 rounded-md font-semibold tabular-nums",
                  p === page ? "bg-cm-gold text-cm-gold-ink" : "text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text",
                )}
              >
                {p + 1}
              </button>
            ),
          )}
          <NavBtn onClick={() => onPageChange(page + 1)} disabled={page >= pages - 1} label="Next page"><ChevronRight size={16} /></NavBtn>
          <NavBtn onClick={() => onPageChange(pages - 1)} disabled={page >= pages - 1} label="Last page"><ChevronsRight size={16} /></NavBtn>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
